"use client"

import { useState, useEffect, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import imageCompression from "browser-image-compression";
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"
import DOMPurify from "dompurify"

import {
  FiTag,
  FiMonitor,
  FiCalendar,
  FiMapPin,
  FiLink,
  FiDollarSign,
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiCheck,
  FiUsers,
  FiX,
  FiUpload,
  FiFilter,
  FiSearch,
  FiChevronRight,
  FiChevronLeft,
} from "react-icons/fi"
import { fetchTactosEvents, tactosCreateEvent, tactosDeleteEvent, tactosUpdateEvent } from "../utils/api"
import { useQuery } from "@tanstack/react-query"

const locations = [
  "Ariyalur",
  "Chengalpattu",
  "Chennai",
  "Coimbatore",
  "Cuddalore",
  "Dharmapuri",
  "Dindigul",
  "Erode",
  "Kallakurichi",
  "Kanchipuram",
  "Kanyakumari",
  "Karur",
  "Krishnagiri",
  "Madurai",
  "Mayiladuthurai",
  "Nagapattinam",
  "Namakkal",
  "Nilgiris",
  "Perambalur",
  "Pudukkottai",
  "Ramanathapuram",
  "Ranipet",
  "Salem",
  "Sivaganga",
  "Tenkasi",
  "Thanjavur",
  "Theni",
  "Thoothukudi",
  "Tiruchirappalli",
  "Tirunelveli",
  "Tirupathur",
  "Tiruppur",
  "Tiruvallur",
  "Tiruvannamalai",
  "Tiruvarur",
  "Vellore",
  "Viluppuram",
  "Virudhunagar",
];

const AdminEventCardSkeleton = () => {
  return (
    <div className="bg-white rounded-3xl p-6 animate-pulse">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-16 h-16 rounded-2xl bg-gray-200" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-1/2 bg-gray-200 rounded" />
          <div className="h-3 w-2/3 bg-gray-200 rounded" />
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="h-3 w-full bg-gray-200 rounded" />
        <div className="h-3 w-5/6 bg-gray-200 rounded" />
        <div className="h-3 w-4/6 bg-gray-200 rounded" />
      </div>

      <div className="flex gap-2">
        <div className="h-8 w-20 bg-gray-200 rounded-lg" />
        <div className="h-8 w-20 bg-gray-200 rounded-lg" />
        <div className="h-8 w-24 bg-gray-200 rounded-lg" />
      </div>
    </div>
  );
};


const isEventNew = (createdAt) => {
  if (!createdAt) return false;
  const eventDate = new Date(createdAt);
  const now = new Date();
  const diffInHours = (now - eventDate) / (1000 * 60 * 60);
  return diffInHours <= 24;
};

const AdminEventDashboard = () => {
  const [activeTab, setActiveTab] = useState("Free")
  const [events, setEvents] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [newField, setNewField] = useState({ label: "", type: "text", required: false, for: "" });
  const [showErrors, setShowErrors] = useState(false);
  const [activeMode, setActiveMode] = useState(null);
  const [modeDetails, setModeDetails] = useState({
    Registered: { amount: "", fields: [] },
    NonRegistered: { amount: "", fields: [] },
  });
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    description: "",
    type: "Free",
    status: "New",
    amount: "",
    mode: "Online",
    date: "",
    time: "",
    link: "",
    location: "",
    address: "",
    logo: "",
    emaildraft: "",
    eventMode: [],
    fields: [],
    isExclusiveEvent: false
  })

  const fieldTypes = [
    { label: "Text Input", value: "text" },
    { label: "Number", value: "number" },
    { label: "Dropdown", value: "select" },
    { label: "Checkbox", value: "checkbox-group" },
    { label: "Radio", value: "radio" },
    { label: "Date", value: "date" },
    { label: "Time", value: "time" },
    { label: "Date & Time", value: "datetime" },
    { label: "File", value: "file" },
    { label: "Image", value: "image" },
    { label: "Toggle", value: "toggle" },
    { label: "Textarea", value: "textarea" },
  ];
  const [editIndex, setEditIndex] = useState(null)
  const navigate = useNavigate()

  // useEffect(() => {
  //   fetchEvents()
  // }, [])

  // const fetchEvents = async () => {
  //   const res = await fetchTactosEvents()
  //   console.log(res)
  //   setEvents(res)
  // }
  const [filters, setFilters] = useState({
    name: "",
    location: "",
    startDate: "",
    mode: "",
  });
  const [loader, setLoader] = useState(false)
  const [page, setPage] = useState(1);
  const { data: tactosEventsData, isLoading, refetch } = useQuery({
    queryKey: ['events', filters, activeTab, page],
    queryFn: () => fetchTactosEvents({ ...filters, activeTab, page, limit: 9 })
  })

  useEffect(() => {
    if (tactosEventsData) {
      setEvents(tactosEventsData)
    }
  }, [tactosEventsData])

  const handleTabChange = (tab) => setActiveTab(tab)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleViewRegistration = (eventId) => {
    navigate(`/admin/registrations/${eventId}`)
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const formattedModeDetails = Object.entries(modeDetails)
      .filter(([mode]) =>
        Array.isArray(formData.eventMode)
          ? formData.eventMode.includes(mode)
          : formData.eventMode === mode
      )
      .map(([mode, details]) => ({
        mode,
        amount: details.amount || "",
        fields: details.fields || [],
      }));

    const allFields = formattedModeDetails.flatMap((m) =>
      m.fields.map((f) => ({ ...f, for: m.mode }))
    );

    // ✅ DO NOT convert eventMode to a string
    const payload = {
      ...formData,
      modeDetails: formattedModeDetails,
      fields: allFields,
    };

    console.log("✅ Final Payload →", payload);

    try {
      if (
        !payload.name ||
        !payload.title ||
        !payload.description ||
        !payload.date ||
        (payload.mode === "Online" && !payload.link) ||
        (payload.mode === "Physical" && !payload.address) ||
        !payload.logo
      ) {
        alert("Please fill all required fields including the event image");
        return;
      }

      setLoader(true)



      if (editIndex !== null) {
        console.log(payload)
        await tactosUpdateEvent(editIndex, payload);
      } else {
        await tactosCreateEvent(payload);
      }

      setLoader(false)

      refetch()
      setShowForm(false);
      setFormData({
        ...formData, name: "",
        title: "",
        description: "",
        emaildraft: "",
        type: "Free",
        status: "New",
        amount: "",
        mode: "Online",
        date: "",
        time: "",
        link: "",
        location: "",
        address: "",
        logo: "",
        eventMode: ["Registered"], // ✅ keep as array
        fields: []
      })

      setEditIndex(null);
    } catch (err) {
      console.error("Form submit failed:", err);
    } finally {
      setLoader(false)
    }
  };


  const handleEdit = (event) => {
    console.log(events);

    // ✅ Registered exists ONLY if not completed
    const registeredExists = events?.upcomingEvents.some(
      (ev) =>
        ev.name === event.name &&
        ev.eventMode?.includes("Registered") &&
        ev.status !== "Completed" &&   // ⭐ KEY FIX
        ev._id !== event._id
    );

    // ✅ NonRegistered exists ONLY if not completed
    const nonRegisteredExists = events?.upcomingEvents.some(
      (ev) =>
        ev.name === event.name &&
        ev.eventMode?.includes("NonRegistered") &&
        ev.status !== "Completed" &&   // ⭐ KEY FIX
        ev._id !== event._id
    );

    const normalizedEvent = {
      ...event,

      // Normalize eventMode to array
      eventMode: Array.isArray(event.eventMode)
        ? event.eventMode
        : event.eventMode
          ? [event.eventMode]
          : [],

      // These flags now IGNORE completed events
      registeredExists,
      nonRegisteredExists
    };

    console.log(normalizedEvent);

    setFormData(normalizedEvent);
    setEditIndex(event._id);
    setShowForm(true);
  };


  const handleChange = async (e) => {
    const { name, type, checked, files, value } = e.target;

    if (name === "logo" && files?.length > 0) {
      let file = files[0];

      // Fix WhatsApp .jpe files
      if (file.name.endsWith(".jpe")) {
        file = new File([file], file.name.replace(".jpe", ".jpg"), {
          type: "image/jpeg"
        });
      }

      // 🔥 FRONTEND FIX: COMPRESS BEFORE BASE64
      try {
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 0.4,            // Keep base64 under Cloudflare’s limit
          maxWidthOrHeight: 1600,    // Resize large images
          useWebWorker: true
        });

        readFileAsBase64(compressedFile);
      } catch (err) {
        console.error("Image compression failed:", err);
      }

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };


  const readFileAsBase64 = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, logo: reader.result }));
    };
    reader.readAsDataURL(file);
  };



  const handleDelete = async (id) => {
    try {
      await tactosDeleteEvent(id)
      refetch()
    } catch (err) {
      console.error("Delete failed:", err)
    }
  }

  const handleStatusChange = async (id) => {
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/events/${id}`, { status: "Completed" })
      refetch()
    } catch (err) {
      console.error("Status update failed:", err)
    }
  }



  const tabVariants = {
    inactive: { scale: 1, opacity: 0.7 },
    active: { scale: 1.05, opacity: 1 },
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    hover: { y: -5, scale: 1.02 },
  }

  const onModalOpenForFields = (data) => {
    setNewField({})
    console.log(data)
    setShowFieldModal(true);
    setNewField({ ...newField, for: data })

  }

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  }

  const getPlainText = (html) => {
    if (!html) return ""
    return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] })
  }







  useEffect(() => {
    // console.log(modeDetails)
    console.log(events)
  }, [events])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6"
        >
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center">
                <FiCalendar className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Tactos Events
                </h1>
                <p className="text-gray-500 text-sm">Manage your events with ease</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowForm(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2"
              >
                <FiPlus className="text-lg" />
                Add New Event
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Tabs Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap gap-3 mb-8 p-2 bg-white rounded-2xl"
        >
          {["Free", "Paid", "Completed"].map((tab) => (
            <motion.button
              key={tab}
              variants={tabVariants}
              animate={activeTab === tab ? "active" : "inactive"}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleTabChange(tab)}
              className={`px-6 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 ${activeTab === tab
                ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              <FiFilter className="text-sm" />
              {tab} Events
            </motion.button>
          ))}
        </motion.div>

        {/* Events Grid */}
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          <AnimatePresence>
            {isLoading ? Array.from({ length: 1 }).map((_, index) => (
              <motion.div
                key={`skeleton-${index}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <AdminEventCardSkeleton />
              </motion.div>
            )) : events?.upcomingEvents?.map((event, index) => (
              <motion.div
                key={event._id}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                whileHover="hover"
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-3xl p-6 relative overflow-hidden"
              >
                {/* Status Badge */}
                <div className="absolute top-4 right-4 z-10">
                  <span
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${event.status === "Completed"
                      ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                      : event.status === "Ongoing"
                        ? "bg-amber-100 text-amber-700 border border-amber-200"
                        : isEventNew(event.createdAt)
                          ? "bg-blue-100 text-blue-700 border border-blue-200"
                          : "hidden"
                      }`}
                  >
                    {event.status}
                  </span>
                </div>

                {/* Event Header */}
                <div className="relative z-10 flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 rounded-2xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 flex-shrink-0 border-2 border-white">
                    {event.logo ? (
                      <img
                        src={event.logo}
                        alt="event"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FiCalendar className="text-gray-400 text-xl" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 mb-1 truncate">{event.name}</h3>
                    <p className="text-sm text-gray-600 truncate">{event.title}</p>
                  </div>
                </div>

                {/* Event Details */}
                <div className="relative z-10 space-y-3 mb-4">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="flex items-center gap-2 text-blue-600">
                      <FiTag className="text-xs" />
                      <span className="font-medium">{event.type}</span>
                    </div>
                    <div className="flex items-center gap-2 text-purple-600">
                      <FiMonitor className="text-xs" />
                      <span className="font-medium">{event.mode}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FiCalendar className="text-green-500 text-xs" />
                    <span>
                      {event.date} • {event.time}
                    </span>
                  </div>

                  {event.mode === "Online" ? (
                    <div className="flex items-center gap-2 text-sm">
                      <FiLink className="text-blue-500 text-xs" />
                      <a
                        href={event.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 hover:underline truncate"
                      >
                        Join Meeting
                      </a>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FiMapPin className="text-red-500 text-xs" />
                        <span className="truncate">{event.location}</span>
                      </div>
                    </div>
                  )}

                </div>

                {/* Description */}
                <div className="relative z-10 mb-6">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {getPlainText(event?.description).length > 80
                      ? `${getPlainText(event?.description).slice(0, 80)}...`
                      : getPlainText(event?.description)}
                  </p>
                </div>


                {/* Action Buttons */}
                <div className="relative z-10 flex flex-wrap gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleEdit(event)}
                    className="flex items-center gap-1 bg-gradient-to-r from-indigo-500 to-blue-500 text-white px-3 py-2 rounded-lg text-xs font-medium"
                  >
                    <FiEdit3 className="text-xs" />
                    Edit
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDelete(event._id)}
                    className="flex items-center gap-1 bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-2 rounded-lg text-xs font-medium"
                  >
                    <FiTrash2 className="text-xs" />
                    Delete
                  </motion.button>

                  {event.status !== "Completed" && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleStatusChange(event._id)}
                      className="flex items-center gap-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-2 rounded-lg text-xs font-medium"
                    >
                      <FiCheck className="text-xs" />
                      Complete
                    </motion.button>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate(`/registrations/${event._id}`)}
                    className="flex items-center gap-1 bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white px-3 py-2 rounded-lg text-xs font-medium"
                  >
                    <FiUsers className="text-xs" />
                    Registrations
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Empty State */}
        {!isLoading && events?.upcomingEvents?.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <FiCalendar className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No events found</h3>
            <p className="text-gray-500 mb-6">Create your first event to get started</p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold"
            >
              Create Event
            </motion.button>
          </motion.div>
        )}

        {events?.pagination?.totalPages > 1 && (
          <div className="flex justify-center items-center gap-6 mt-10">

            {/* PREVIOUS */}
            <motion.button
              whileHover={page !== 1 ? { scale: 1.06 } : {}}
              whileTap={page !== 1 ? { scale: 0.94 } : {}}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className={`
      px-5 py-2 rounded-full
      font-semibold flex items-center gap-1
      border transition-all duration-200
      ${page === 1
                  ? "border-gray-300 text-gray-400 cursor-not-allowed"
                  : "border-black text-black  hover:bg-gradient-to-r from-blue-600 to-purple-600 hover:text-white hover:border-none"
                }
    `}
            >
              <FiChevronLeft className="text-lg" />
              Prev
            </motion.button>

            {/* PAGE INDICATOR */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="
      px-6 py-2 rounded-full
     bg-gradient-to-r from-blue-600 to-purple-600
      text-white font-bold tracking-wide
      shadow-lg
    "
            >
              Page {events.pagination.currentPage} / {events.pagination.totalPages}
            </motion.div>

            {/* NEXT */}
            <motion.button
              whileHover={page !== events.pagination.totalPages ? { scale: 1.06 } : {}}
              whileTap={page !== events.pagination.totalPages ? { scale: 0.94 } : {}}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              disabled={page === events.pagination.totalPages}
              onClick={() => setPage(p => p + 1)}
              className={`
      px-5 py-2 rounded-full
      font-semibold flex items-center gap-1
      border transition-all duration-200
      ${page === events.pagination.totalPages
                  ? "border-gray-300 text-gray-400 cursor-not-allowed"
                  : "border-black text-black hover:bg-gradient-to-r from-blue-600 to-purple-600 hover:text-white hover:border-none "
                }
    `}
            >
              Next
              <FiChevronRight className="text-lg" />
            </motion.button>

          </div>
        )}

      </div>

      {/* Form Modal  Event Adding*/}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    {editIndex !== null ? "Edit Event" : "Create New Event"}
                  </h2>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setShowForm(false)
                      setEditIndex(null)
                      setFormData({
                        ...formData, name: "",
                        title: "",
                        description: "",
                        emaildraft: "",
                        type: "Free",
                        status: "New",
                        amount: "",
                        mode: "Online",
                        date: "",
                        time: "",
                        link: "",
                        location: "",
                        address: "",
                        logo: "",
                        eventMode: ["Registered"], // ✅ keep as array
                        fields: []
                      })
                    }}
                    className="w-10 h-10 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center transition-colors duration-300"
                  >
                    <FiX className="text-gray-600 hover:text-red-600" />
                  </motion.button>
                </div>
              </div>

              <form onSubmit={handleFormSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Event Name */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Event Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter event name"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                      required
                    />
                  </div>

                  {/* Title */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Hosted By *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter host name"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                      required
                    />
                  </div>

                  {/* Type */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Event Type *</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                      required
                    >
                      <option value="Free">Free</option>
                      <option value="Paid">Paid</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Status *</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                      required
                    >
                      <option value="New">New</option>
                      <option value="Ongoing">Ongoing</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </div>

                  {/* Conditional Paid Fields */}
                  {/* {formData.type === "Paid" && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Amount (₹) *</label>
                        <input
                          type="text"
                          name="amount"
                          value={formData.amount}
                          onChange={handleInputChange}
                          placeholder="Enter amount"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                          required
                        />
                      </div>
                    </>
                  )} */}

                  {/* Mode */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Event Mode *</label>
                    <select
                      name="mode"
                      value={formData.mode}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                      required
                    >
                      <option value="Online">Online</option>
                      <option value="Physical">Physical</option>
                    </select>
                  </div>

                  {/* Conditional Mode Fields */}
                  {formData.mode === "Online" ? (
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Meeting Link *</label>
                      <input
                        type="text"
                        name="link"
                        value={formData.link}
                        onChange={handleInputChange}
                        placeholder="Enter meeting link"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                        required
                      />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">District *</label>
                        <select
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                        >
                          <option value="">Select District</option>
                          {locations.map((location) => (
                            <option key={location} value={location}>
                              {location}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Address</label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="Enter full address (optional)"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                        />
                      </div>
                    </>
                  )}

                  {/* Date */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Date *</label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                      required
                    />
                  </div>

                  {editIndex &&
                    formData.eventMode.length === 1 &&
                    formData.eventMode[0] === "NonRegistered" &&
                    !formData.registeredExists && (
                      <div className="md:col-span-2 border p-4  rounded-xl">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.enableRegistered || false}
                            onChange={(e) =>
                              setFormData({ ...formData, enableRegistered: e.target.checked })
                            }
                          />
                          <span className="font-medium text-gray-700">
                            Enable Registered Users
                          </span>
                        </label>
                      </div>
                    )}


                  {editIndex &&
                    formData.eventMode.length === 1 &&
                    formData.eventMode[0] === "Registered" &&
                    !formData.nonRegisteredExists && (
                      <div className="md:col-span-2 border p-4 rounded-xl">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.enableNonRegistered || false}
                            onChange={(e) =>
                              setFormData({ ...formData, enableNonRegistered: e.target.checked })
                            }
                          />
                          <span className="font-medium text-gray-700">
                            Enable NonRegistered Users
                          </span>
                        </label>
                      </div>
                    )}



                  {!editIndex &&
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Access Mode *</label>
                      <div className="flex flex-col gap-2">
                        {["Registered", "NonRegistered"].map((mode) => (
                          <label key={mode} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.eventMode.includes(mode)}
                              onChange={(e) => {
                                const newModes = e.target.checked
                                  ? [...formData.eventMode, mode]
                                  : formData.eventMode.filter((m) => m !== mode)
                                setFormData({ ...formData, eventMode: newModes })
                              }}
                              className="w-4 h-4 accent-blue-600"
                            />
                            <span className="text-gray-700">{mode} User</span>
                          </label>
                        ))}
                      </div>
                    </div>}

                  <div className="md:col-span-2 border p-4 rounded-xl">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.isExclusiveEvent || false}
                        onChange={(e) =>
                          setFormData({ ...formData, isExclusiveEvent: e.target.checked })
                        }
                      />
                      <span className="font-medium text-gray-700">
                        Exclusive
                      </span>
                    </label>
                  </div>

                  {formData.eventMode.map((mode) => (
                    <div key={mode} className="border p-4 rounded-lg bg-gray-50 mt-4">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        {mode} User Details
                      </h4>

                      {/* Amount if Paid */}
                      {formData.type === "Paid" && (
                        <div className="space-y-2 mb-3">
                          <label className="text-sm font-semibold text-gray-700">
                            Amount for {mode} User
                          </label>
                          <input
                            type="text"
                            value={modeDetails[mode]?.amount || ""}
                            onChange={(e) =>
                              setModeDetails((prev) => ({
                                ...prev,
                                [mode]: { ...prev[mode], amount: e.target.value },
                              }))
                            }
                            placeholder="Enter amount"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      )}

                      {/* Add Field Button per mode */}
                      <button
                        type="button"
                        className="px-3 py-1 bg-blue-500 text-white rounded-md"
                        onClick={() => onModalOpenForFields(mode)}
                      >
                        Add Fields for {mode}
                      </button>

                      {/* List fields */}
                      {modeDetails[mode]?.fields?.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {modeDetails[mode].fields.map((f, i) => (
                            <li key={i} className="flex justify-between bg-white p-2 rounded border">
                              <span>{f.label} ({f.type})</span>
                              <button
                                className="text-red-500"
                                onClick={() =>
                                  setModeDetails((prev) => ({
                                    ...prev,
                                    [mode]: {
                                      ...prev[mode],
                                      fields: prev[mode].fields.filter((_, idx) => idx !== i),
                                    },
                                  }))
                                }
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}



                  {/* <div className="mt-7 flex items-center float-right pt-1 border-t border-gray-100">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                     onClick={() => setShowFieldModal(true)}
                    className="w-[150px] float-right bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 rounded-xl font-semibold text-lg"
                  >
                    Add Fields
                  </motion.button>
                </div> */}

                  <AnimatePresence>
                    {showFieldModal && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                      >
                        <motion.div
                          initial={{ scale: 0.9 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0.9 }}
                          className="bg-white rounded-2xl w-full max-w-md p-6 shadow-lg"
                        >
                          <h3 className="text-xl font-semibold mb-4 text-gray-700">
                            Add Custom Field
                          </h3>

                          <div className="space-y-4">
                            {/* Field Label */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Field Label <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={newField.label}
                                onChange={(e) =>
                                  setNewField({ ...newField, label: e.target.value })
                                }
                                placeholder="e.g. Age, Department"
                                className={`w-full px-4 py-2 border ${!newField.label && showErrors
                                  ? "border-red-500"
                                  : "border-gray-300"
                                  } rounded-lg focus:ring-2 focus:ring-blue-400 outline-none`}
                              />
                              {!newField.label && showErrors && (
                                <p className="text-red-500 text-sm mt-1">
                                  Field label is required.
                                </p>
                              )}
                            </div>

                            {/* Field Type */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Field Type <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={newField.type}
                                onChange={(e) =>
                                  setNewField({ ...newField, type: e.target.value })
                                }
                                className={`w-full px-4 py-2 border ${!newField.type && showErrors
                                  ? "border-red-500"
                                  : "border-gray-300"
                                  } rounded-lg focus:ring-2 focus:ring-blue-400 outline-none`}
                              >
                                <option value="">Select a type</option>
                                {fieldTypes.map((each) => (
                                  <option key={each.value} value={each.value}>
                                    {each.label}
                                  </option>
                                ))}
                              </select>
                              {!newField.type && showErrors && (
                                <p className="text-red-500 text-sm mt-1">
                                  Field type is required.
                                </p>
                              )}
                            </div>

                            {/* Required Checkbox */}
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={newField.required}
                                onChange={(e) =>
                                  setNewField({ ...newField, required: e.target.checked })
                                }
                                className="w-4 h-4 accent-blue-600"
                              />
                              <label className="text-sm text-gray-700">Required Field</label>
                            </div>
                          </div>

                          {/* Buttons */}
                          <div className="mt-6 flex justify-end gap-3">
                            <button
                              type="button"
                              onClick={() => setShowFieldModal(false)}
                              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                              Cancel
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                if (!newField.label || !newField.type) {
                                  setShowErrors(true);
                                  return;
                                }

                                setModeDetails((prev) => ({
                                  ...prev,
                                  [newField.for]: {
                                    ...prev[newField.for],
                                    fields: [...(prev[newField.for]?.fields || []), newField],
                                  },
                                }))

                                // Reset after add
                                setNewField({ label: "", type: "", required: false });
                                setShowErrors(false);
                                setShowFieldModal(false);
                              }}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                              Add
                            </button>
                          </div>
                        </motion.div>
                      </motion.div>
                    )}

                  </AnimatePresence>

                  {formData.fields.length > 0 && (
                    <div className="md:col-span-2 mt-1">
                      <h4 className="font-semibold text-gray-700 mb-3">Custom Fields Added:</h4>

                      {/* Group fields by "for" (Registered / NonRegistered) */}
                      {["Registered", "NonRegistered"].map((mode) => {
                        const modeFields = formData.fields.filter((f) => f.for === mode);
                        if (modeFields.length === 0) return null;

                        return (
                          <div key={mode} className="mb-4">
                            <h5 className="font-semibold text-blue-600 mb-2">
                              {mode} User Fields
                            </h5>

                            <ul className="space-y-2">
                              {modeFields.map((f, i) => (
                                <li
                                  key={i}
                                  className="flex items-center justify-between bg-gray-50 border border-gray-200 px-4 py-2 rounded-lg"
                                >
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                                    <span className="text-gray-800 font-medium">
                                      {f.label}{" "}
                                      <span className="text-gray-500 text-sm">
                                        ({f.type})
                                      </span>
                                    </span>

                                    <span
                                      className={`text-sm font-medium ${f.required ? "text-green-600" : "text-gray-500"
                                        }`}
                                    >
                                      {f.required ? "Required" : "Optional"}
                                    </span>
                                  </div>

                                  <button
                                    onClick={() =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        fields: prev.fields.filter((_, idx) => idx !== i),
                                      }))
                                    }
                                    className="text-red-500 hover:text-red-700 text-sm font-medium"
                                  >
                                    Remove
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  )}



                  {/* Time */}
                  {/* <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Time *</label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                      required
                    />
                  </div> */}

                  {/* Description */}
                  <div className="md:col-span-2 space-y-1">
                    <label className="text-sm font-semibold text-gray-700">Description *</label>
                    <ReactQuill
                      theme="snow"
                      modules={quillModules}
                      value={formData.description}
                      onChange={(value) =>
                        setFormData({ ...formData, description: value })
                      }
                    />

                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Email Draft (Optional)</label>
                    <textarea
                      name="emaildraft"
                      value={formData.emaildraft}
                      onChange={handleInputChange}
                      placeholder="Enter event email draft"
                      rows={4}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 resize-none"
                      required
                    />
                  </div>

                  {/* Logo Upload */}
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Event Image *</label>
                    {!formData.logo && (
                      <p className="text-sm text-red-500 mb-2">An event image is required</p>
                    )}
                    <div className="relative">
                      <input
                        type="file"
                        name="logo"
                        accept="image/*"
                        onChange={handleChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className={`w-full px-4 py-8 border-2 border-dashed rounded-xl hover:border-blue-400 transition-all duration-300 flex flex-col items-center justify-center gap-3 ${formData.logo ? "bg-green-50 border-green-300" : "bg-gray-50 border-gray-300 hover:bg-blue-50/50"
                        }`}>
                        {formData.logo ? (
                          <>
                            <FiCheck className="text-2xl text-green-500" />
                            <p className="text-sm font-medium text-gray-700">Image selected</p>
                          </>
                        ) : (
                          <>
                            <FiUpload className="text-2xl text-gray-400" />
                            <div className="text-center">
                              <p className="text-sm font-medium text-gray-700">Click to upload image</p>
                              <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {formData.logo && (
                      <div className="mt-2 w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                        <img
                          src={formData.logo}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <motion.button
                    whileHover={{ scale: loader ? 1 : 1.02 }}
                    whileTap={{ scale: loader ? 1 : 0.98 }}
                    type="submit"
                    disabled={loader}
                    className={`w-full py-4 rounded-xl font-semibold text-lg text-white
      bg-gradient-to-r from-blue-600 to-purple-600
      flex items-center justify-center gap-3
      ${loader ? "opacity-70 cursor-not-allowed" : ""}
    `}
                  >
                    {loader ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      editIndex !== null ? "Update Event" : "Create Event"
                    )}
                  </motion.button>
                </div>

              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default AdminEventDashboard